import { useCallback, useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { TASK_STATUS } from "@services/tasksApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { QUERY_KEY } from "@services/vesselNominationApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useTask } from "../../hooks/useTask";
import type { VesselNominationDecision } from "../../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  nominationId: string;
  type: keyof typeof TASK_STATUS;
  onDecisionComplete?: (decision: VesselNominationDecision) => void;
};

const LANG_BASE = "vessel-nomination.nomination-details.approval";
const TEST_ID_BASE = "vessel-nomination-approval";

const DRAWER_TEXT = {
  [TASK_STATUS.APPROVED]: {
    title: `${LANG_BASE}.approveTitle`,
    description: `${LANG_BASE}.approveDescription`,
    submit: `${LANG_BASE}.approve`,
    successTitle: `${LANG_BASE}.approved-success-title`,
    errorTitle: `${LANG_BASE}.approved-error-title`,
  },
  [TASK_STATUS.REJECTED]: {
    title: `${LANG_BASE}.rejectTitle`,
    description: `${LANG_BASE}.rejectDescription`,
    submit: `${LANG_BASE}.reject`,
    successTitle: `${LANG_BASE}.rejected-success-title`,
    errorTitle: `${LANG_BASE}.rejected-error-title`,
  },
};

const FORM_FIELDS = {
  NOTE: "noteText",
  ATTACHMENT: "nominationApprovalAttachment",
  ATTACHMENT_NAME: "nominationApprovalAttachmentName",
} as const;

const getUploadEntryUrl = (entry?: UploadEntry) =>
  entry && "url" in entry && typeof entry.url === "string" ? entry.url : "";

const getUploadEntryName = (entry?: UploadEntry) =>
  entry && "name" in entry && typeof entry.name === "string" ? entry.name : "";

const getApproveRejectSchema = (t: TFunction, type: keyof typeof TASK_STATUS) =>
  z.object({
    [FORM_FIELDS.NOTE]: z
      .string()
      .min(
        type === TASK_STATUS.APPROVED ? 0 : 1,
        t("form-validation.mandatory", { label: t(`${LANG_BASE}.notes-to-agent`) }),
      )
      .max(
        1000,
        t("form-validation.max-length", {
          label: t(`${LANG_BASE}.notes-to-agent`),
          max: 1000,
        }),
      )
      .optional(),
    [FORM_FIELDS.ATTACHMENT]: z.string().optional(),
    [FORM_FIELDS.ATTACHMENT_NAME]: z.string().optional(),
  });

const ApproveRejectSideDrawer = ({
  isOpen,
  onClose,
  nominationId,
  type,
  onDecisionComplete,
}: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const attachmentFile = filesByKey[FORM_FIELDS.ATTACHMENT] || [];
  const initialAttachmentUrl = getUploadEntryUrl(attachmentFile[0]);
  const initialAttachmentName = initialAttachmentUrl ? getUploadEntryName(attachmentFile[0]) : "";

  const uploadConditions = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = uploadConditions.format.map((ext) => `.${ext.toLowerCase()}`);

  const c = useMemo(() => DRAWER_TEXT[type], [type]);
  const latestDecisionRef = useRef<VesselNominationDecision | null>(null);

  const form = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getApproveRejectSchema(t, type)),
    shouldUnregister: false,
    defaultValues: {
      [FORM_FIELDS.NOTE]: "",
      [FORM_FIELDS.ATTACHMENT]: initialAttachmentUrl,
      [FORM_FIELDS.ATTACHMENT_NAME]: initialAttachmentName,
    },
  });

  const handleDrawerClose = () => {
    onClose();
  };

  const handleCancel = () => {
    form.reset();
    clearFilesForKey(FORM_FIELDS.ATTACHMENT);
    onClose();
  };

  const handleSuccess = () => {
    if (latestDecisionRef.current) {
      onDecisionComplete?.(latestDecisionRef.current);
    }

    handleCancel();

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
      queryClient.refetchQueries({
        queryKey: ["nomination", "bff-vessel", nominationId],
        exact: true,
      }),
    ]).catch(() => undefined);
  };

  const handleError = () => {
    handleCancel();
  };

  useEffect(() => {
    if (!form.getValues(FORM_FIELDS.ATTACHMENT)) {
      clearFilesForKey(FORM_FIELDS.ATTACHMENT);
    }
  }, [clearFilesForKey, form]);

  const { completeTask, isCompleteTaskPending } = useTask({ handleSuccess, handleError });

  const onValid = async ({
    noteText,
    nominationApprovalAttachment,
    nominationApprovalAttachmentName,
  }: {
    noteText?: string;
    nominationApprovalAttachment?: string;
    nominationApprovalAttachmentName?: string;
  }) => {
    if (attachmentUpload.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    latestDecisionRef.current = {
      decisionStatus: type,
      noteText,
      attachmentUrl: nominationApprovalAttachment,
      documentName: nominationApprovalAttachmentName,
    };
    completeTask({
      nominationId,
      params: {
        decisionStatus: type,
        noteText,
        attachmentUrl: nominationApprovalAttachment,
        documentName: nominationApprovalAttachmentName,
      },
      successTitle: t(`${c.successTitle}`),
      errorTitle: t(`${c.errorTitle}`),
    });
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  const mutationFn = async ({ file, documentType }: { file: File; documentType: string }) => {
    const payloadForSasToken = {
      prefix: SasTokenPrefix.VESSEL_NOMINATION,
      contentType: file.type || null,
      privilege: SasTokenPrivilege.WRITE,
      blobUrl: null,
    };

    const { blobUrl, sasToken } = await getSasToken(payloadForSasToken);

    const [uploadResult] = await uploadFileToAzureBlob({
      file,
      blobUrl,
      sasToken,
      certificateType: documentType,
    });

    const documentUrl = uploadResult?.documentUrl ?? blobUrl;
    return { documentUrl, documentType };
  };

  const attachmentUpload = useMutation({
    mutationFn,
  });

  const handleAttachmentUpload = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;

      attachmentUpload.mutate(
        { file, documentType: "NOMINATION_APPROVAL_ATTACHMENT" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(FORM_FIELDS.ATTACHMENT, documentUrl);
            form.setValue(FORM_FIELDS.ATTACHMENT_NAME, file.name);
            setFilesForKey(FORM_FIELDS.ATTACHMENT, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(FORM_FIELDS.ATTACHMENT);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, attachmentUpload, form, notify, setFilesForKey, t],
  );

  const handleRemoveAttachmentFile = useCallback(() => {
    form.setValue(FORM_FIELDS.ATTACHMENT, "");
    form.setValue(FORM_FIELDS.ATTACHMENT_NAME, "");
    clearFilesForKey(FORM_FIELDS.ATTACHMENT);
  }, [clearFilesForKey, form]);

  return (
    <FormProvider {...form}>
      <SideDrawer
        isOpen={isOpen}
        onClose={handleDrawerClose}
        onCancel={handleCancel}
        onSubmit={onSubmit}
        keepMounted={true}
        title={t(`${c?.title}`)}
        description={t(`${c?.description}`)}
        submitLabel={t(`${c?.submit}`)}
        isSubmitting={isCompleteTaskPending}
      >
        <div className="w-[400px] p-6 flex flex-col gap-6">
          <FormItem
            name={FORM_FIELDS.NOTE}
            type="textarea"
            label={t(`${LANG_BASE}.notes-to-agent`)}
            testId={`${TEST_ID_BASE}-comments`}
            minRows={4}
            maxChars={1000}
            showCharCounter
          />
          {attachmentFile[0] ? (
            <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
              <Box sx={{ mb: 1 }}>
                <CustomFormLabel>{t(`${LANG_BASE}.attachments`)}</CustomFormLabel>
              </Box>
              <SingleFile
                testId={`${TEST_ID_BASE}-attachment-file`}
                file={attachmentFile[0]}
                fieldName={attachmentFile[0].name}
                currentFileIndex={0}
                setFiles={handleRemoveAttachmentFile}
                isLoading={attachmentUpload.isPending}
              />
            </div>
          ) : (
            <DocumentUpload
              name={FORM_FIELDS.ATTACHMENT}
              label={t(`${LANG_BASE}.attachments`)}
              testId={`${TEST_ID_BASE}-approve-reject-attachments`}
              allowedExtensions={allowedSideDrawerExtensions}
              conditions={uploadConditions}
              maxFiles={1}
              onUpload={handleAttachmentUpload}
              setFiles={(next) => setFilesForKey(FORM_FIELDS.ATTACHMENT, next as UploadEntry[])}
              files={attachmentFile}
            />
          )}
        </div>
      </SideDrawer>
    </FormProvider>
  );
};

export default ApproveRejectSideDrawer;
